(ns io.daskul.tutorial.webapp.views.invoice-create
  "Custom invoice create — full page and inline (guardian tab) variants.

   Full page: select customer → fetch billables → pick items → submit.
   Inline: guardian is known from parent context → fetch billables → pick items → submit.

   Used when invoice aggregate has :submit-endpoint (bypasses auto-generated CRUD)."
  (:require
   [uix.core :as uix :refer [defui $]]
   [uix.re-frame :as urf]
   [re-frame.core :as rf]
   [io.daskul.webapp.api.http :as api]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.tutorial.webapp.router :as router]
   ["dayjs" :as dayjs]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.widgets.billing.line-editor :as le]
   [io.daskul.webapp.widgets.billing.line-editor-logic :as le-logic]
   [io.daskul.webapp.widgets.billing.customer-picker :as cp]))

;; ============================================================================
;; Helpers
;; ============================================================================

(defn- fetch-billables
  "Fetch billable items for a customer. Calls on-success with response map."
  [customer-type customer-id on-success on-error]
  (api/request!
   {:method :get
    :path "/billing/billables"
    :params {:customer-type customer-type
             :customer-id customer-id}
    :on-success on-success
    :on-error (fn [_] (on-error "Failed to load billable items"))}))

;; ============================================================================
;; Create Page
;; ============================================================================

(defui create-page [{:keys [config]}]
  (let [route (urf/use-subscribe [::router/current-route])
        ;; Read query params for pre-fill (from the #834 unbilled banner's
        ;; Create Invoice link, which passes the resolved payer)
        query-customer-type (get-in route [:query-params :customer-type])
        query-customer-id (get-in route [:query-params :customer-id])
        query-customer-name (get-in route [:query-params :customer-name])

        ;; Build initial customer from query params if present
        initial-customer (when (and query-customer-type query-customer-id)
                           {:customer-type query-customer-type
                            :customer-id query-customer-id
                            :customer-name query-customer-name})

        ;; Local state — the line editor is controlled over this {:lines
        ;; :available} map. Enrollment billables auto-populate :lines; Add charge
        ;; appends a row; removing an enrollment line parks it in :available (the
        ;; billable set is fixed) so it can be re-included. The whole set submits
        ;; at once ("settle before create").
        [customer set-customer!] (uix/use-state initial-customer)
        [editor set-editor!] (uix/use-state {:lines [] :available []})
        [discounts set-discounts!] (uix/use-state [])
        seq-ref (uix/use-ref 0)
        [loading-billables? set-loading-billables!] (uix/use-state false)
        [error set-error!] (uix/use-state nil)
        [due-date set-due-date!] (uix/use-state nil)
        [notes set-notes!] (uix/use-state "")
        [submitting? set-submitting!] (uix/use-state false)
        [attempted? set-attempted!] (uix/use-state false) ; surface required-field errors after a Create attempt

        {:keys [lines available]} editor
        customer-type (:customer-type customer)
        customer-id (some-> (:customer-id customer) str)

        items (mapv le-logic/line->item lines)
        invalid (le-logic/lines-invalid lines)

        refetch-billables!
        (uix/use-callback
         (fn []
           (when (and customer-type customer-id)
             (set-loading-billables! true)
             (fetch-billables customer-type customer-id
                             (fn [resp]
                               (let [ds (or (:discounts resp) [])]
                                 (set-discounts! ds)
                                 (set-editor! {:lines (-> (le-logic/billables->lines (or (:panels resp) []))
                                                          (le-logic/apply-auto-discounts ds))
                                               :available []}))
                               (set-loading-billables! false))
                             (fn [msg]
                               (set-error! msg)
                               (set-loading-billables! false)))))
         [customer-type customer-id])

        handle-customer-change
        (fn [c]
          (set-customer! c)
          (set-editor! {:lines [] :available []})
          (set-discounts! [])
          (set-error! nil))

        add-line!
        (fn [kind]
          (let [n @seq-ref]
            (reset! seq-ref (inc n))
            ;; unit-price left blank on purpose — it's required, so the field
            ;; starts empty rather than a stand-in 0.
            (set-editor! (fn [st] (update st :lines conj {:kind kind
                                                          :line-id (str (name kind) ":" n)
                                                          :description ""
                                                          :quantity 1})))))

        edit-line!
        (fn [line-id patch]
          (set-editor! (fn [st] (update st :lines
                                        (fn [ls] (mapv (fn [l] (if (= (:line-id l) line-id) (merge l patch) l)) ls))))))

        remove-line!
        (fn [line-id]
          (set-editor! (fn [st] (le-logic/exclude-line st line-id))))

        include-line!
        (fn [line-id]
          (set-editor! (fn [st] (le-logic/include-line st line-id))))

        handle-submit
        (fn []
          (if (seq invalid)
            (set-attempted! true) ; block create, surface the red required fields
            (do
              (set-attempted! false)
              (set-submitting! true)
              (set-error! nil)
              (api/request!
           {:method :post
            :path "/billing/invoices"
            :params {:customer-type customer-type
                     :customer-id customer-id
                     :items items
                     :due-date due-date
                     :notes (when (seq notes) notes)}
            :on-success (fn [invoice]
                          (set-submitting! false)
                          (router/navigate! :aggregate/detail
                                            {:agg "invoices" :id (:id invoice)}))
            :on-error (fn [err]
                        (set-submitting! false)
                        (let [body (:response err)]
                          (set-error! (or (:message body)
                                          "Failed to create invoice"))))}))))]

    ;; When customer changes: fetch billables
    (uix/use-effect
     (fn []
       (refetch-billables!)
       js/undefined)
     [refetch-billables!])

    ($ :<>
      ($ ui/title {:order 3 :mb "xs"} "New Invoice")

      ($ ui/grid {:gutter "md"}
        ($ ui/grid-col {:span #js {:base 12 :md 8}}
          ($ ui/card {:shadow "sm" :padding "lg" :radius "md" :withBorder true}
            ($ ui/stack {:gap "md"}
              ;; One-time discount note — above the whole form
              ($ ui/info-alert {}
                "Discounts added to a line are one-time — they apply to this invoice only. To create a reusable discount, use Billing → Discounts.")

              ;; Customer picker
              ($ cp/customer-picker
                {:value customer-id
                 :initial-name (:customer-name customer)
                 :on-change handle-customer-change})

              ;; Error display
              (when error
                ($ ui/error-alert error))

              (cond
                ;; No customer selected
                (not customer)
                ($ ui/text {:c "dimmed" :size "sm"}
                  "Select a customer to see billable items.")

                ;; Loading billables
                loading-billables?
                ($ ui/centered-loader {:py "md" :size "sm"})

                ;; Editable line list + form fields
                :else
                ($ :<>
                  ($ le/line-editor
                    {:lines lines
                     :available available
                     :discounts discounts
                     :invalid (when attempted? invalid)
                     :on-edit edit-line!
                     :on-remove remove-line!
                     :on-include include-line!
                     :on-add add-line!})

                  ($ ui/divider {:my "sm"})
                  (let [date-value (when (and due-date (seq (str due-date)))
                                     (.toDate (dayjs due-date)))]
                    ($ ui/date-picker-input
                      {:label "Due Date"
                       :value date-value
                       :onChange (fn [date-obj]
                                   (if date-obj
                                     (set-due-date! (.format (dayjs date-obj) "YYYY-MM-DD"))
                                     (set-due-date! nil)))
                       :valueFormat "MMM D, YYYY"
                       :placeholder "Pick a date"
                       :highlightToday true
                       :clearable true}))
                  ($ ui/textarea
                    {:label "Notes"
                     :placeholder "Optional notes for this invoice"
                     :value notes
                     :onChange #(set-notes! (.. % -target -value))
                     :minRows 2
                     :autosize true})

                  ;; Action buttons
                  ($ ui/group {:justify "flex-end" :mt "md" :gap "sm"}
                    ($ ui/button
                      {:variant "subtle" :color "gray"
                       :onClick #(router/navigate! :aggregate/list {:agg "invoices"})}
                      "Cancel")
                    ($ ui/button
                      {:onClick handle-submit
                       :loading submitting?
                       :disabled (empty? lines)}
                      "Create Invoice")))))))))))

;; ============================================================================
;; Inline variant — embedded in guardian's Invoices tab
;; ============================================================================

(defui inline-from-guardian
  "Inline invoice create for the guardian's Invoices tab. The guardian is known
   from parent context (no customer picker); otherwise this is the create-page
   flow — the SAME shared line-editor, in-memory until submit-all via
   POST /billing/invoices.

   Props (from child-aggregate-list custom-add-component):
   - :parent-agg-key, :parent-id, :child-agg-key, :fk-field
   - :on-close — callback to close the add form
   - :parent-record — guardian record (has :person-id)"
  [{:keys [parent-agg-key parent-id child-agg-key fk-field
           on-close parent-record]}]
  (let [person-id (:person-id parent-record)
        [editor set-editor!] (uix/use-state {:lines [] :available []})
        [discounts set-discounts!] (uix/use-state [])
        seq-ref (uix/use-ref 0)
        [loading? set-loading!] (uix/use-state false)
        [error set-error!] (uix/use-state nil)
        [due-date set-due-date!] (uix/use-state nil)
        [notes set-notes!] (uix/use-state "")
        [submitting? set-submitting!] (uix/use-state false)
        [attempted? set-attempted!] (uix/use-state false) ; surface required-field errors after a Create attempt
        {:keys [lines available]} editor
        items (mapv le-logic/line->item lines)
        invalid (le-logic/lines-invalid lines)

        refetch-billables!
        (uix/use-callback
         (fn []
           (when person-id
             (set-loading! true)
             (api/request!
              {:method :get
               :path "/billing/billables"
               :params {:customer-type "guardian" :customer-id parent-id}
               :on-success (fn [resp]
                             (let [ds (or (:discounts resp) [])]
                               (set-discounts! ds)
                               (set-editor! {:lines (-> (le-logic/billables->lines (or (:panels resp) []))
                                                        (le-logic/apply-auto-discounts ds))
                                             :available []})
                               (set-loading! false)))
               :on-error (fn [_]
                           (set-error! "Failed to load billable items")
                           (set-loading! false))})))
         [person-id parent-id])

        add-line!
        (fn [kind]
          (let [n @seq-ref]
            (reset! seq-ref (inc n))
            ;; unit-price left blank on purpose — required, so the field starts empty.
            (set-editor! (fn [st] (update st :lines conj {:kind kind
                                                          :line-id (str (name kind) ":" n)
                                                          :description "" :quantity 1})))))
        edit-line!
        (fn [line-id patch]
          (set-editor! (fn [st] (update st :lines
                                        (fn [ls] (mapv #(if (= (:line-id %) line-id) (merge % patch) %) ls))))))
        remove-line! (fn [line-id] (set-editor! (fn [st] (le-logic/exclude-line st line-id))))
        include-line! (fn [line-id] (set-editor! (fn [st] (le-logic/include-line st line-id))))

        handle-submit
        (fn []
          (if (seq invalid)
            (set-attempted! true)
            (do
              (set-attempted! false)
              (set-submitting! true)
              (set-error! nil)
              (api/request!
               {:method :post
                :path "/billing/invoices"
                :params {:customer-type "guardian"
                         :customer-id parent-id
                         :items items
                         :due-date due-date
                         :notes (when (seq notes) notes)}
                :on-success (fn [_invoice]
                              (set-submitting! false)
                              (rf/dispatch [::agg.events/list-children
                                            parent-agg-key parent-id child-agg-key fk-field])
                              (on-close))
                :on-error (fn [err]
                            (set-submitting! false)
                            (set-error! (or (get-in err [:response :message])
                                            "Failed to create invoice")))}))))]

    ;; Fetch billables on mount
    (uix/use-effect
     (fn []
       (refetch-billables!)
       js/undefined)
     [refetch-billables!])

    ($ ui/box {:p "md"
               :style {:backgroundColor "var(--mantine-color-gray-0)"
                       :borderRadius "var(--mantine-radius-sm)"}}
      ($ ui/stack {:gap "md"}
        (when error
          ($ ui/error-alert error))

        (cond
          (not person-id)
          ($ ui/text {:c "dimmed"} "Guardian has no linked person.")

          loading?
          ($ ui/centered-loader {:py "md" :size "sm"})

          :else
          ($ :<>
            ($ le/line-editor
              {:lines lines
               :available available
               :discounts discounts
               :invalid (when attempted? invalid)
               :on-edit edit-line!
               :on-remove remove-line!
               :on-include include-line!
               :on-add add-line!})

            ($ ui/divider {:my "sm"})
            (let [date-value (when (and due-date (seq (str due-date)))
                               (.toDate (dayjs due-date)))]
              ($ ui/date-picker-input
                {:label "Due Date"
                 :value date-value
                 :onChange (fn [date-obj]
                             (if date-obj
                               (set-due-date! (.format (dayjs date-obj) "YYYY-MM-DD"))
                               (set-due-date! nil)))
                 :valueFormat "MMM D, YYYY"
                 :placeholder "Pick a date"
                 :highlightToday true
                 :clearable true}))
            ($ ui/textarea
              {:label "Notes"
               :placeholder "Optional notes for this invoice"
               :value notes
               :onChange #(set-notes! (.. % -target -value))
               :minRows 2
               :autosize true})))

        ;; Action buttons
        ($ ui/group {:justify "flex-end" :gap "sm"}
          ($ ui/button {:variant "subtle" :color "gray" :onClick on-close}
            "Cancel")
          (when (not loading?)
            ($ ui/button
              {:onClick handle-submit
               :loading submitting?
               :disabled (empty? lines)}
              "Create Invoice")))))))
